import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { getMyBills, payBill } from '@/services/api';
import { 
  IndianRupee, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  FileText,
  AlertCircle,
  CreditCard,
  Dumbbell,
  Home,
  Zap
} from 'lucide-react';

const TenantBills = () => {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    loadBills();
  }, []);

  const loadBills = async () => {
    try {
      setLoading(true);
      const data = await getMyBills();
      setBills(data);
    } catch (error) {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  };

  const handlePayBill = async (billId) => {
    try {
      setPayingId(billId);
      await payBill(billId);
      toast.success('Payment successful!');
      loadBills();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setPayingId(null);
    }
  };

  const getBillTypeIcon = (billType) => {
    switch (billType?.toLowerCase()) {
      case 'amenity':
      case 'amenity_booking':
        return <Dumbbell className="h-5 w-5" />;
      case 'maintenance':
        return <Home className="h-5 w-5" />;
      case 'utility':
        return <Zap className="h-5 w-5" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
      case 'pending':
      case 'unpaid':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Unpaid</Badge>;
      case 'partial':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Partial</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const pendingBills = bills.filter(b => b.status === 'pending' || b.status === 'unpaid');
  const paidBills = bills.filter(b => b.status === 'paid');

  const totalDue = pendingBills.reduce((sum, b) => sum + (b.amount || 0), 0);
  const totalPaid = paidBills.reduce((sum, b) => sum + (b.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-bills">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Bills & Payments</h1>
        <p className="text-slate-600 mt-1">View and pay your society bills</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className={pendingBills.length > 0 ? "border-red-200 bg-red-50" : "border-green-200 bg-green-50"}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${pendingBills.length > 0 ? 'text-red-600' : 'text-green-600'}`}>Total Due</p>
                <p className={`text-2xl font-bold flex items-center gap-1 ${pendingBills.length > 0 ? 'text-red-700' : 'text-green-700'}`}>
                  <IndianRupee className="h-5 w-5" />
                  {totalDue.toLocaleString('en-IN')}
                </p>
              </div>
              {pendingBills.length > 0 ? (
                <AlertCircle className="h-8 w-8 text-red-400" />
              ) : (
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Total Paid</p>
                <p className="text-2xl font-bold text-green-700 flex items-center gap-1">
                  <IndianRupee className="h-5 w-5" />
                  {totalPaid.toLocaleString('en-IN')}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Pending Bills</p>
                <p className="text-2xl font-bold text-blue-700">{pendingBills.length}</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Bills */}
      {pendingBills.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="bg-amber-50 rounded-t-lg">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Clock className="h-5 w-5" />
              Pending Bills
            </CardTitle>
            <CardDescription className="text-amber-600">
              Pay these bills to avoid late fees
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {pendingBills.map((bill) => (
                <div
                  key={bill.id}
                  className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200"
                  data-testid={`pending-bill-${bill.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-amber-100 rounded-lg text-amber-600">
                      {getBillTypeIcon(bill.bill_type)}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{bill.title || bill.bill_type}</p>
                      <p className="text-sm text-slate-500">
                        {bill.description || `Bill for ${bill.bill_type}`}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Due: {bill.due_date ? new Date(bill.due_date).toLocaleDateString('en-IN') : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-slate-900 flex items-center gap-1">
                        <IndianRupee className="h-4 w-4" />
                        {bill.amount?.toLocaleString('en-IN')}
                      </p>
                      {getStatusBadge(bill.status)}
                    </div>
                    <Button
                      onClick={() => handlePayBill(bill.id)}
                      disabled={payingId === bill.id}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`pay-bill-${bill.id}`}
                    >
                      {payingId === bill.id ? 'Processing...' : 'Pay Now'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paid Bills */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-secondary" />
            Payment History
          </CardTitle>
          <CardDescription>Your completed bill payments</CardDescription>
        </CardHeader>
        <CardContent>
          {paidBills.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" />
              <p>No payment history yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paidBills.map((bill) => (
                <div key={bill.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg text-green-600">
                      {getBillTypeIcon(bill.bill_type)}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{bill.title || bill.bill_type}</p>
                      <p className="text-xs text-slate-500">
                        Paid: {bill.paid_date ? new Date(bill.paid_date).toLocaleDateString('en-IN') : 'N/A'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 flex items-center gap-1">
                      <IndianRupee className="h-3 w-3" />
                      {bill.amount?.toLocaleString('en-IN')}
                    </span>
                    {getStatusBadge(bill.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantBills;
